/**
 * Canonical list of `debtos:*` localStorage keys that hold user data.
 *
 * **This list is the contract.** Every key that holds *user data* —
 * data the user has explicitly entered or that represents their
 * portfolio state — must be in this list so JSON export/import
 * round-trips capture it. Adding a new persistence key elsewhere
 * REQUIRES adding it here too.
 *
 * The invariant test in `tests/unit/repository/dataBackup.test.ts`
 * pins the canonical list — drift between this and the repos' actual
 * storage keys fails CI loudly at the point of introduction.
 *
 * Deliberately NOT included (intentional fresh state on restore):
 * - `debtos:activeMonth:v1` — UI navigation, not user data
 * - `debtos:welcomed:v1` — onboarding flag
 * - `debtos:demoBannerDismissedAt:v1` — banner suppression TTL
 * - `debtos:migratedAt:v1` — cloud migration flag
 * - `debtos:syncMeta:v1` — managed by DataBackup itself
 *
 * Retired keys (cleaned up on boot in App.tsx, no longer written):
 * - `debtos:enteredApp:v1` — demo-mode entry flag, replaced in v0.8.16
 *   by `hasUserData()` derivation + sessionStorage opt-in. The legacy
 *   value is removed from localStorage on every app boot.
 *
 * **Why this is its own file**: `react-refresh/only-export-components`
 * requires component files to export only components for Fast Refresh
 * to work. The DataBackup component imports this constant; tests
 * import it directly. Keeping it in its own module keeps both happy.
 */
export const BACKUP_KEYS = [
  'debtos:debts:v1',
  'debtos:incomeSources:v1',
  'debtos:ledger:v1',
  'debtos:notifications:v1',
  'debtos:recurringExpenses:v1',
  // Added v0.8.6: pre-existing bug — recovery anchor was silently
  // omitted from backups, so a "restore from JSON" lost the user's
  // anchor date + starting total. Anchors are small (single object)
  // but load-bearing for the Dashboard's Recovery Progress headline.
  'debtos:recoveryAnchor:v1',
] as const

export type BackupKey = (typeof BACKUP_KEYS)[number]

/**
 * **v0.8.27 — privacy:** wipe every user-data localStorage key so a
 * post-sign-out demo session can't render the previous account's real
 * data. The leak path that motivates this: the v0.8.14 DataBackup
 * restore writes user data to BOTH localStorage AND cloud while
 * signed in (so signed-in restore stays signed in, by design). Without
 * a sign-out wipe, the same browser's anonymous next-visit reads that
 * localStorage and the demo page shows real numbers. The founder hit
 * this in production — second time in the v0.8.x arc; v0.8.14 closed
 * the restore-time half of it.
 *
 * Trade-off explicitly accepted: anonymous demo data accumulated
 * BEFORE sign-in is also wiped. Privacy > convenience — a user who
 * cared about that demo state would have exported it.
 *
 * UI prefs (`activeMonth`, `activeModule`, sync metadata) are
 * intentionally NOT wiped — they're not sensitive and the user
 * losing their view-state on every sign-out would be obnoxious.
 *
 * Best-effort: any single key's removeItem failure (private mode,
 * storage disabled) is swallowed so it can't block the sign-out flow.
 */
export function clearUserDataLocalStorage(): void {
  if (typeof window === 'undefined') return
  for (const key of BACKUP_KEYS) {
    try {
      localStorage.removeItem(key)
    } catch {
      /* swallow — see JSDoc */
    }
  }
}
